import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getTeacherAssignments } from '../../services/lessonService';
import { StudentPracticeProgressDialog } from './StudentPracticeProgressDialog';

interface Assignment {
  assignment_id: string;
  module_id: string;
  module_title: string;
  module_subject: string;
  module_grade_level: string;
  student_id: string;
  student_name: string;
  student_email: string;
  status: string;
  due_date: string | null;
  assigned_at: string;
  completed_at: string | null;
  updated_at: string;
  instructions: string | null;
}

interface ModuleGroup {
  module_id: string;
  module_title: string;
  module_subject: string;
  module_grade_level: string;
  assignments: Assignment[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`progress-tabpanel-${index}`}
      aria-labelledby={`progress-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const StudentProgress: React.FC = () => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [modules, setModules] = useState<ModuleGroup[]>([]);
  const [statusSummary, setStatusSummary] = useState({
    assigned: 0,
    in_progress: 0,
    completed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tabValue, setTabValue] = useState(0);
  const [progressStudent, setProgressStudent] = useState<Assignment | null>(null);

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTeacherAssignments('me');
      setAssignments(data.assignments);
      setModules(data.modules);
      setStatusSummary(data.status_summary);
    } catch (err: any) {
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'assigned':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'in_progress':
        return <PlayCircleIcon fontSize="small" />;
      case 'assigned':
        return <HourglassEmptyIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'assigned':
        return 'Not Started';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredAssignments = assignments.filter((assignment) => {
    const matchesSearch =
      assignment.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.module_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.module_subject.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || assignment.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Student Progress
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Track the progress of your students across all assigned modules
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Assignments
                  </Typography>
                  <Typography variant="h4">{assignments.length}</Typography>
                </Box>
                <AssignmentIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    In Progress
                  </Typography>
                  <Typography variant="h4">{statusSummary.in_progress}</Typography>
                </Box>
                <PlayCircleIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Completed
                  </Typography>
                  <Typography variant="h4">{statusSummary.completed}</Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by student name, module, or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="assigned">Not Started</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All Assignments" />
          <Tab label="By Module" />
        </Tabs>
      </Box>

      {/* All Assignments View */}
      <TabPanel value={tabValue} index={0}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Grade</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAssignments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" py={3}>
                      No assignments found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAssignments.map((assignment) => (
                  <TableRow key={assignment.assignment_id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {assignment.student_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {assignment.student_email}
                      </Typography>
                    </TableCell>
                    <TableCell>{assignment.module_title}</TableCell>
                    <TableCell>{assignment.module_subject}</TableCell>
                    <TableCell>{assignment.module_grade_level}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(assignment.status)}
                        label={getStatusLabel(assignment.status)}
                        color={getStatusColor(assignment.status)}
                        size="small"
                        onClick={() => setProgressStudent(assignment)}
                        aria-label={`View ${assignment.student_name}'s progress`}
                      />
                    </TableCell>
                    <TableCell>{formatDate(assignment.assigned_at)}</TableCell>
                    <TableCell>{formatDate(assignment.completed_at)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setProgressStudent(assignment)}
                      >
                        View progress
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* By Module View */}
      <TabPanel value={tabValue} index={1}>
        {modules.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary" align="center">
              No modules with assignments found
            </Typography>
          </Paper>
        ) : (
          modules.map((module) => (
            <Paper key={module.module_id} sx={{ mb: 3, p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {module.module_title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {module.module_subject} • {module.module_grade_level}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Student</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Assigned</TableCell>
                        <TableCell>Completed</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {module.assignments.map((assignment) => (
                        <TableRow key={assignment.assignment_id}>
                          <TableCell>
                            <Typography variant="body2">{assignment.student_name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(assignment.status)}
                              label={getStatusLabel(assignment.status)}
                              color={getStatusColor(assignment.status)}
                              size="small"
                              onClick={() => setProgressStudent(assignment)}
                              aria-label={`View ${assignment.student_name}'s progress`}
                            />
                          </TableCell>
                          <TableCell>{formatDate(assignment.assigned_at)}</TableCell>
                          <TableCell>{formatDate(assignment.completed_at)}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => setProgressStudent(assignment)}
                            >
                              View progress
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          ))
        )}
      </TabPanel>
      <StudentPracticeProgressDialog
        open={progressStudent !== null}
        studentId={progressStudent?.student_id || null}
        studentName={progressStudent?.student_name}
        onClose={() => setProgressStudent(null)}
      />
    </Box>
  );
};

export default StudentProgress;
